/**
 * Pure derivation and copy for the system banner strip.
 *
 * The banner is the only surface that tells an operator whether the platform is
 * acting on the outside world at all, so every sentence it can show lives here
 * rather than inline in `SystemBanner.tsx`. The workspace ships no DOM test
 * environment, which makes copy written inside a component untestable by
 * construction. Keeping the words in a plain module is what lets the honesty
 * rules below be enforced by `SystemBanner.model.test.ts`.
 *
 * The rule those tests exist to hold: no sentence may tell an operator the
 * platform is sending unless the health report says sending is actually on. The
 * previous wording said "Everything still sends" from a check about brand
 * knowledge retrieval, which read as a statement about the whole system and was
 * false the entire time dry run was on.
 */
import type { HealthReport } from '../lib/types'
import { formatDateTime } from '../lib/format'

// ---------------------------------------------------------------------------
// Health checks
// ---------------------------------------------------------------------------

export type CheckState = 'ok' | 'degraded' | 'failing'

export interface CheckResult {
  name: string
  state: CheckState
  detail?: string
}

/** The health endpoint reports a string per check, and older shapes a boolean. */
function stateOf(value: unknown): CheckState {
  if (typeof value === 'boolean') return value ? 'ok' : 'failing'
  if (typeof value === 'string') {
    if (value === 'ok' || value === 'up') return 'ok'
    return value === 'degraded' ? 'degraded' : 'failing'
  }
  if (typeof value === 'object' && value !== null) {
    const record = value as { ok?: boolean; state?: string }
    if (record.state !== undefined) return stateOf(record.state)
    return record.ok === true ? 'ok' : 'failing'
  }
  return 'failing'
}

export function normalizeChecks(report: HealthReport | undefined): CheckResult[] {
  if (!report?.checks) return []
  return Object.entries(report.checks).map(([name, value]) => {
    const detail = typeof value === 'object' && value !== null ? (value as { detail?: string }).detail : undefined
    return { name, state: stateOf(value), ...(detail === undefined ? {} : { detail }) }
  })
}

const FRIENDLY: Record<string, string> = {
  db: 'the database',
  redis: 'the counter store',
  queues: 'the job queues',
  embeddings: 'brand knowledge retrieval',
  voyage: 'brand knowledge retrieval',
}

/** A check's name in operator words, falling back to the wire name. */
export function friendlyName(check: string): string {
  return FRIENDLY[check] ?? check
}

function list(names: readonly string[]): string {
  if (names.length === 0) return 'part of the platform'
  if (names.length === 1) return names[0] as string
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

// ---------------------------------------------------------------------------
// Sending mode
// ---------------------------------------------------------------------------

/**
 * Whether the platform is allowed to act on the outside world.
 *
 * `unknown` is a real and supported answer: an API that predates the dry-run
 * field reports nothing about it. The copy treats that as "make no claim in
 * either direction" rather than assuming sending is on, because assuming is how
 * this banner came to say something untrue in the first place.
 */
export type SendingMode = 'live' | 'dry_run' | 'unknown'

export function readSendingMode(report: HealthReport | undefined): SendingMode {
  if (report?.dryRun === true) return 'dry_run'
  if (report?.dryRun === false) return 'live'
  return 'unknown'
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

export const DRY_RUN_HEADLINE = 'Dry run is on.'

/**
 * What dry run means in the terms an operator cares about.
 *
 * Three things have to be said together, because saying any one of them alone
 * misleads. Nothing leaves the platform. Everything inside it still happens,
 * including the quota spend, which is why a dry-run day still uses the day's
 * allowance. And there is one named thing to change to make it send, so the
 * operator is not left guessing whether this is a fault to chase.
 */
export const DRY_RUN_BODY =
  'Nothing is being sent to anyone outside the platform. No email goes out, no manual card is ' +
  'created, and no work reaches a device. Everything else runs normally, so campaigns move, copy ' +
  'is generated, and every quota and safety limit is counted as though the message had gone out. ' +
  'To start sending, set DRY_RUN to false in the deployment environment and restart the platform.'

export const FAILING_HEADLINE = 'Something is down.'

/**
 * A dependency being unreachable says nothing about whether sending is on, so
 * this sentence talks about the limits and the record, never about sending
 * carrying on.
 */
export function describeFailing(checks: readonly string[]): string {
  return (
    `The platform cannot reach ${list(checks.map(friendlyName))}. Safety and quota limits still ` +
    'apply, and anything that cannot be recorded will not be sent.'
  )
}

export const DEGRADED_HEADLINE = 'Running on a fallback.'

/** What a degraded check actually costs, in the words of the person it costs it to. */
const DEGRADED_CONSEQUENCE: Record<string, string> = {
  embeddings:
    'Brand knowledge is being retrieved by the offline fallback, so generated copy is picking weaker passages than usual.',
  voyage:
    'Brand knowledge is being retrieved by the offline fallback, so generated copy is picking weaker passages than usual.',
}

/**
 * The second half of the degraded sentence, which is where the old wording went
 * wrong. A degraded check is a statement about that check, so what it may claim
 * about the rest of the platform depends on what the rest of the platform is
 * doing. Under dry run it may not claim sending at all, and when the report does
 * not say, it claims nothing either way.
 */
const FALLBACK_SCOPE: Record<SendingMode, string> = {
  live: 'The fallback does not stop anything on its own, so sending carries on and every limit still applies.',
  dry_run: 'The fallback does not stop anything on its own, and nothing is being sent while dry run is on.',
  unknown: 'The fallback does not stop anything on its own, and every limit still applies.',
}

export function describeDegraded(checks: readonly string[], mode: SendingMode): string {
  const consequence =
    checks.map((check) => DEGRADED_CONSEQUENCE[check]).find((text): text is string => text !== undefined) ??
    `${list(checks.map(friendlyName))} is degraded rather than down, so results may be weaker than usual.`
  return `${consequence} ${FALLBACK_SCOPE[mode]}`
}

// ---------------------------------------------------------------------------
// The writing allowance
// ---------------------------------------------------------------------------

export const WRITING_PAUSED_HEADLINE = 'Writing is paused.'

/**
 * What a spent model allowance stops, said before somebody presses a button
 * that cannot work.
 *
 * On 2026-09-13 the subscription every model call runs on was used up for the
 * week, and the first anybody learned of it was a refusal after pressing
 * describe. This names the things that stop and says when they come back, but
 * only when the service itself said so. Otherwise it says when a request will
 * next ask, because nothing on the platform asks on its own. It says nothing
 * about sending, because a spent allowance does not change whether anything is
 * sent and this strip may sit beside the dry-run one.
 *
 * It names only what a person can reach from a screen today. Changing a
 * sequence with a sentence has no screen yet, so it is not listed.
 *
 * And it names only what the row it reads knows about. The row is written and
 * cleared by the model calls the console and the API make, describing a
 * sequence or a workflow, writing a template, regenerating a justification, and
 * since 2026-09-14 by the worker's message drafting too. Before that the worker
 * neither read nor cleared the row, so drafts were left out of this sentence: a
 * strip naming them stayed up after drafts were being written again, and in a
 * spent week where nobody pressed a console button it never appeared at all.
 * Drafts are named again because the worker now keeps the same row.
 */
export function describeWritingPaused(
  allowance: NonNullable<HealthReport['writingAllowance']>,
  formatWhen: (iso: string) => string = (iso) => formatDateTime(iso),
): string {
  const spent = 'The model subscription this platform writes with has used its allowance'
  const stops = 'Describing a sequence, writing new templates, regenerating a justification and writing message drafts will not work'
  const kept = 'A draft that could not be written waits for a person in the approval queue, and anything already written stays exactly as it is.'
  if (allowance.resetKnown && allowance.resumeAt !== null) {
    return `${spent}. ${stops} until ${formatWhen(allowance.resumeAt)}. ${kept}`
  }
  // An API from before `nextTryAt` sent the probe time in `resumeAt`.
  const next = allowance.nextTryAt !== undefined ? allowance.nextTryAt : allowance.resumeAt
  return next === null
    ? `${spent} and has not said when it comes back. ${stops} for now, and the next request asks it again. ${kept}`
    : `${spent} and has not said when it comes back. ${stops} for now, and the first request after ${formatWhen(next)} asks it again. ${kept}`
}

// ---------------------------------------------------------------------------
// The strips
// ---------------------------------------------------------------------------

export type BannerTone = 'danger' | 'warning' | 'info'

export interface BannerStrip {
  key: 'dry-run' | 'failing' | 'degraded' | 'writing-paused'
  tone: BannerTone
  headline: string
  body: string
}

/**
 * Every strip the shell should show, top first and already in order.
 *
 * Dry run comes first and is its own strip rather than being folded into the
 * health wording, because it is not a health state and must stay on screen while
 * a check is also degraded. That combination is not hypothetical: it is the
 * running production configuration, and it is exactly the case where the health
 * strip alone used to be the only thing said about sending.
 *
 * The two health states still share one strip, with failing winning, so the
 * shell never grows an unbounded stack of warnings.
 */
export function systemBanners(
  report: HealthReport | undefined,
  options: { formatWhen?: (iso: string) => string } = {},
): BannerStrip[] {
  if (!report) return []

  const strips: BannerStrip[] = []
  if (readSendingMode(report) === 'dry_run') {
    strips.push({ key: 'dry-run', tone: 'info', headline: DRY_RUN_HEADLINE, body: DRY_RUN_BODY })
  }

  const checks = normalizeChecks(report)
  const failing = checks.filter((check) => check.state === 'failing').map((check) => check.name)
  const degraded = checks.filter((check) => check.state === 'degraded').map((check) => check.name)

  // A reported status of `down` with no per-check detail still has to show.
  const wholeSystemDown = report.status === 'down' || (report.status === 'degraded' && checks.length === 0)

  if (failing.length > 0 || wholeSystemDown) {
    strips.push({ key: 'failing', tone: 'danger', headline: FAILING_HEADLINE, body: describeFailing(failing) })
  }

  // After the health strips, because a dependency being down is the more urgent
  // news, and before nothing else, because it is the one strip that tells
  // somebody a button they are about to press will refuse. `unknown` shows
  // nothing: a report that could not read the row says nothing about the
  // subscription, and a strip claiming it was spent would be a guess.
  const allowance = report.writingAllowance
  if (allowance?.state === 'spent') {
    strips.push({
      key: 'writing-paused',
      tone: 'warning',
      headline: WRITING_PAUSED_HEADLINE,
      body: describeWritingPaused(allowance, options.formatWhen),
    })
  }

  // A degraded check no longer gets a strip. It sat on every screen every day,
  // said nothing an operator could act on, and a warning nobody can act on is
  // how a shell teaches people to skip past the strip that matters. The state
  // is still reported: `/healthz` carries it, and the wording below is kept and
  // still tested so the health screen can say it in the one place it belongs.
  void degraded

  return strips
}

// ---------------------------------------------------------------------------
// Honesty guards
// ---------------------------------------------------------------------------

/**
 * Phrases that assert the platform is acting on the outside world. None of them
 * may appear in anything shown while sending is off or unknown. They are listed
 * lowercase and matched against lowercased copy.
 */
export const FORBIDDEN_SENDING_CLAIMS: readonly string[] = [
  'everything still sends',
  'everything is still sending',
  'still sends',
  'sending carries on',
  'sending continues',
  'messages are still going out',
  'messages still go out',
]

/**
 * Dry run is a deliberate configuration, and very often the correct one, so the
 * notice may not read as an accusation. An operator who is told they made a
 * mistake starts looking for a fault instead of reading the sentence.
 */
export const FORBIDDEN_BLAME_CLAIMS: readonly string[] = [
  'you forgot',
  'you failed',
  'you should have',
  'by mistake',
  'misconfigured',
  'incorrectly',
  'wrongly',
]
